import React, { useEffect, useState, useRef, useCallback } from "react";
import "./GradeLimit.css";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import useAuthStore from "../../services/authStore";
import { toastSuccess, toastError } from "../../services/toaster";

export default function ExpenseGradeLimit() {
  const [tableData, setTableData] = useState([]);
  const [grades, setGrades] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const hasFetched = useRef(false);
  const Regenerated_expireToken = useCallback(async (apiCall, retry = true) => {
    try {
      return await apiCall();
    } catch (err) {
      if (err?.response?.status === 401 && retry) {
        const store = useAuthStore.getState();

        const newToken = await store.generateIntegrationToken();

        if (!newToken) {
          toastError("Session expired. Please login again.");
          return null;
        }

        // 🧠 IMPORTANT: wait a tick so Zustand updates
        await new Promise((res) => setTimeout(res, 0));

        // ✅ call again AFTER store update
        return await apiCall();
      }
      throw err;
    }
  }, []);

  // ✅ FETCH ORG LEVELS
  const fetchOrgLevels = useCallback(async () => {
    try {
      const res = await Regenerated_expireToken(async () => {
        const { integrationToken, integrationBaseUrl } =
          useAuthStore.getState();

        return await axios.get(`${integrationBaseUrl}/api/organization_level`, {
          headers: { Authorization: `Bearer ${integrationToken}` },
        });
      });

      if (!res) return [];

      const data = res.data?.data || [];

      setOrgLevels(data);

      const dynamicGrades = data.map((g) => g.id);
      setGrades(dynamicGrades);

      return dynamicGrades;
    } catch (err) {
      console.error("Org level error:", err);
      toastError("Failed to load grades");
      return [];
    }
  }, [Regenerated_expireToken]);

  // ✅ FETCH DATA
  const fetchData = useCallback(
    async (dynamicGradesParam) => {
      try {
        setLoading(true);

        const { token, companyId } = useAuthStore.getState();
        const dynamicGrades = dynamicGradesParam || grades;

        if (!token || !companyId || dynamicGrades.length === 0) {
          console.warn("Missing dependencies for fetch");
          return;
        }

        const res = await axios.get(
          `${API_URL}/expense-grade-limit/${companyId}?grades=${dynamicGrades.join(",")}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setTableData(res.data || []);
      } catch (err) {
        console.error("Fetch error:", err);
        toastError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    },
    [API_URL, grades],
  );

  // ✅ INIT LOAD (NO DOUBLE CALL)
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const init = async () => {
      const dynamicGrades = await fetchOrgLevels();
      await fetchData(dynamicGrades);
    };

    init();
  }, [fetchOrgLevels, fetchData]);

  // ✅ SAFE STATE UPDATE (NO MUTATION)
  // const handleLimitChange = (rowIndex, gradeCode, field, value) => {
  //   setTableData((prev) =>
  //     prev.map((row, idx) => {
  //       if (idx !== rowIndex) return row;

  //       return {
  //         ...row,
  //         limits: row.limits.map((l) =>
  //           l.expensegradecode === gradeCode
  //             ? { ...l, [field]: value, isconfigured: true }
  //             : l,
  //         ),
  //       };
  //     }),
  //   );
  // };

  const handleLimitChange = (rowIndex, gradeCode, field, value) => {
    setTableData((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;

        return {
          ...row,
          limits: row.limits.map((limit) => {
            if (String(limit.expensegradecode) !== String(gradeCode)) {
              return limit;
            }

            return {
              ...limit,
              [field]: value,
              isconfigured:
                value !== "" && value !== null && value !== undefined,
            };
          }),
        };
      }),
    );
  };

  // ✅ BULK UPDATE
  const handleBulkUpdate = async () => {
    try {
      setSaveLoading(true);

      const { token } = useAuthStore.getState();
      if (!token) return;

      const user = jwtDecode(token);
      const companyId = user.company_id;

      const payload = tableData.map((row) => ({
        expensetypeid: row.expensetypeid,
        limits: row.limits.map((l) => ({
          expensegradecode: l.expensegradecode,
          currency: l.currency || row.currency || user.currency,
          limitamount:
            l.limitamount === "" || l.limitamount === null
              ? null
              : Number(l.limitamount),
        })),
      }));

      await axios.patch(
        `${API_URL}/expense-grade-limit/${companyId}?grades=${grades.join(",")}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toastSuccess("All data updated successfully");
    } catch (err) {
      console.error("Bulk update error:", err);
      toastError("Update failed");
    } finally {
      setSaveLoading(false);
    }
  };

  // ✅ LOADER
  if (loading) {
    return (
      <div className="grade-limit-container">
        <div className="center-loader">
          <FaSpinner className="spinner" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }
  const formatIndianNumber = (value) => {
    if (!value) return "";

    const [integerPart, decimalPart] = String(value).split(".");

    const lastThree = integerPart.slice(-3);

    const otherNumbers = integerPart.slice(0, -3);

    const formattedInteger =
      otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
      (otherNumbers ? "," : "") +
      lastThree;

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  const formatInternationalNumber = (value) => {
    if (!value) return "";

    const [integerPart, decimalPart] = String(value).split(".");

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };
  return (
    <div className="grade-limit-container">
      {/* TABLE WRAPPER */}

      <div className="grade-table-wrapper">
        <table className="grade-table">
          <thead>
            <tr>
              <th>Expense Type</th>
              <th>GL Code</th>
              {orgLevels.map((g, i) => (
                <th key={i} className={`grade-header grade-${g.title}`}>
                  <div>{g.title}</div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={grades.length + 3} className="no-data">
                  No Data Available
                </td>
              </tr>
            ) : (
              tableData.map((row, rowIndex) => (
                <tr key={row.expensetypeid}>
                  <td title={row.expensetypename}>
                    {row.expensetypename || "-"}
                  </td>

                  <td>
                    <span className="gl-badge">{row.glcode || "-"}</span>
                  </td>

                  {grades.map((grade, i) => {
                    const limit = row.limits.find(
                      (l) => l.expensegradecode === grade,
                    );

                    return (
                      <td key={i}>
                        <input
                          className="grade-input"
                          type="text"
                          value={
                            limit?.limitamount !== null &&
                            limit?.limitamount !== undefined
                              ? (() => {
                                  const currency =
                                    limit?.currency || row.currency || "INR";

                                  const clean = String(
                                    limit.limitamount,
                                  ).replace(/,/g, "");

                                  if (currency === "INR") {
                                    return formatIndianNumber(clean);
                                  }

                                  return formatInternationalNumber(clean);
                                })()
                              : ""
                          }
                          placeholder="N/A"
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");

                            if (!/^\d*\.?\d{0,2}$/.test(rawValue)) {
                              return;
                            }

                            handleLimitChange(
                              rowIndex,
                              grade,
                              "limitamount",
                              rawValue,
                            );
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="grade-footer">
        <button
          className="bulk-update-btn"
          onClick={handleBulkUpdate}
          disabled={saveLoading}
        >
          {saveLoading ? "Updating..." : "Update"}
        </button>
      </div>
    </div>
  );
}
